import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Info, Search, CheckCircle, XCircle, Loader2, Edit, Users, Clock, ExternalLink, AlertCircle } from 'lucide-react';
import { Card, CardContent } from '../../../components/ui/Card';
import Button from '../../../components/ui/Button';
import {
  getProfileDirectory,
  bulkUpdateProfileStatus,
  type ProfileDirectoryItemView,
  ProfileStatus,
} from '../../../api/PublicProfile/publicProfileApi';

interface ProfileDirectoryTabProps {
  selectedPersonaId: string;
  onSettingsChange?: () => void;
}

const ProfileDirectoryTab: React.FC<ProfileDirectoryTabProps> = ({ selectedPersonaId, onSettingsChange }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [profiles, setProfiles] = useState<ProfileDirectoryItemView[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedProfiles, setSelectedProfiles] = useState<number[]>([]);
  const [actionInProgress, setActionInProgress] = useState(false);
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const showToast = (type: 'success' | 'error', message: string) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 5000);
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const statusFilter = filterStatus === 'all' ? undefined : filterStatus;
      const result = await getProfileDirectory(statusFilter, undefined, searchTerm || undefined);

      if (result.status === 200 && result.payload) {
        setProfiles(result.payload);
      } else {
        showToast('error', t('Failed to load profiles'));
      }
    } catch (error) {
      console.error('Error loading profiles:', error);
      showToast('error', t('Failed to load profiles'));
    } finally {
      setLoading(false);
    }
  }, [filterStatus, searchTerm, t]);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      loadData();
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [loadData]);

  const getStatusColor = (status: ProfileStatus) => {
    switch (status) {
      case ProfileStatus.Active:
        return 'bg-success/10 text-success';
      case ProfileStatus.Draft:
        return 'bg-muted text-muted-foreground';
      case ProfileStatus.PendingApproval:
        return 'bg-warning/10 text-warning';
      case ProfileStatus.Disabled:
        return 'bg-destructive/10 text-destructive';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const getStatusLabel = (status: ProfileStatus, isPublic: boolean) => {
    if (status === ProfileStatus.Active && isPublic) {
      return t('Public');
    }
    return t(status);
  };

  const handleSelectProfile = (id: number) => {
    setSelectedProfiles((prev) =>
      prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (selectedProfiles.length === profiles.length) {
      setSelectedProfiles([]);
    } else {
      setSelectedProfiles(profiles.map((p) => p.id));
    }
  };

  const handleBulkAction = async (action: string) => {
    if (selectedProfiles.length === 0) return;

    setActionInProgress(true);
    try {
      const result = await bulkUpdateProfileStatus(selectedProfiles, action);
      if (result.status === 200) {
        showToast('success', t(`Successfully updated ${selectedProfiles.length} profile(s)`));
        setSelectedProfiles([]);
        loadData();
        onSettingsChange?.();
      } else {
        showToast('error', t('Failed to update profiles'));
      }
    } catch (error) {
      console.error('Error performing bulk action:', error);
      showToast('error', t('Failed to update profiles'));
    } finally {
      setActionInProgress(false);
    }
  };

  const handleViewProfile = (profile: ProfileDirectoryItemView) => {
    if (profile.profileSlug) {
      window.open(`/p/${profile.profileSlug}`, '_blank');
    }
  };

  const handleEditProfile = (profile: ProfileDirectoryItemView) => {
    // Navigate to the employee's profile configuration center
    navigate(`/profile-configuration-center/${profile.ownerId}`);
  };

  // Calculate statistics
  const stats = {
    total: profiles.length,
    active: profiles.filter(p => p.status === ProfileStatus.Active && p.isPublic).length,
    pending: profiles.filter(p => p.status === ProfileStatus.PendingApproval).length,
    draft: profiles.filter(p => p.status === ProfileStatus.Draft).length,
  };

  if (loading && profiles.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
        <span className="ms-3 text-muted-foreground">{t('Loading profiles...')}</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Toast Notification */}
      {toast && (
        <div className={`fixed bottom-8 left-1/2 transform -translate-x-1/2 z-50 px-4 py-3 rounded-lg shadow-lg flex items-center gap-2
                        ${toast.type === 'success'
                          ? 'bg-success/10 text-success'
                          : 'bg-destructive/10 text-destructive'}`}>
          {toast.type === 'success' ? (
            <CheckCircle className="w-5 h-5" />
          ) : (
            <AlertCircle className="w-5 h-5" />
          )}
          <span className="font-medium">{toast.message}</span>
        </div>
      )}

      {/* Persona Info Banner */}
      {selectedPersonaId && (
        <div className="bg-primary/10 border border-primary/20 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <Info className="w-4 h-4 text-primary" />
            <p className="text-sm text-primary">
              {t('Manage all employee public profiles in your company.')}
            </p>
          </div>
        </div>
      )}

      {/* Search and Filters */}
      <Card variant="elevated">
        <CardContent>
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="w-5 h-5 absolute start-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
                <input
                  type="text"
                  placeholder={t('Search by name or email...')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full ps-10 pe-4 py-2 border border-input rounded-lg focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                />
              </div>
            </div>

            <div className="flex gap-2">
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-4 py-2 border border-input rounded-lg focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
              >
                <option value="all">{t('All Status')}</option>
                <option value="Active">{t('Active')}</option>
                <option value="PendingApproval">{t('Pending Approval')}</option>
                <option value="Draft">{t('Draft')}</option>
                <option value="Disabled">{t('Disabled')}</option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Bulk Actions */}
      {selectedProfiles.length > 0 && (
        <div className="bg-primary/10 border border-primary/20 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-primary">
              {t(`${selectedProfiles.length} profile(s) selected`)}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleBulkAction('approve')}
                disabled={actionInProgress}
                className="min-h-[44px]"
                iconName="CheckCircle"
              >
                {t('Approve')}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleBulkAction('activate')}
                disabled={actionInProgress}
                className="min-h-[44px]"
                iconName="CheckCircle"
              >
                {t('Activate')}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleBulkAction('deactivate')}
                disabled={actionInProgress}
                className="min-h-[44px]"
                iconName="XCircle"
              >
                {t('Deactivate')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Profile Directory Table */}
      <Card variant="elevated" className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-muted">
              <tr>
                <th className="px-6 py-3 text-start">
                  <input
                    type="checkbox"
                    checked={selectedProfiles.length === profiles.length && profiles.length > 0}
                    onChange={handleSelectAll}
                    className="w-4 h-4 text-primary rounded focus-visible:ring-ring"
                  />
                </th>
                <th className="px-6 py-3 text-start text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  {t('Employee')}
                </th>
                <th className="px-6 py-3 text-start text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  {t('Department')}
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  {t('Status')}
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  {t('Views')}
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  {t('Contacts')}
                </th>
                <th className="px-6 py-3 text-end text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  {t('Actions')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-card divide-y divide-border">
              {profiles.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-muted-foreground">
                    {t('No profiles found')}
                  </td>
                </tr>
              ) : (
                profiles.map((profile) => (
                  <tr key={profile.id} className="hover:bg-muted/50">
                    <td className="px-6 py-4">
                      <input
                        type="checkbox"
                        checked={selectedProfiles.includes(profile.id)}
                        onChange={() => handleSelectProfile(profile.id)}
                        className="w-4 h-4 text-primary rounded focus-visible:ring-ring"
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {profile.ownerPhotoUrl ? (
                          <img
                            src={profile.ownerPhotoUrl}
                            alt={profile.ownerName}
                            className="w-10 h-10 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                            <span className="text-muted-foreground text-sm font-medium">
                              {profile.ownerName.charAt(0)}
                            </span>
                          </div>
                        )}
                        <div className="ms-3">
                          <div className="text-sm font-medium text-foreground">{profile.ownerName}</div>
                          <div className="text-sm text-muted-foreground">{profile.ownerEmail}</div>
                          {profile.jobTitle && (
                            <div className="text-xs text-muted-foreground">{profile.jobTitle}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-foreground">{profile.departmentName || '-'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(profile.status)}`}>
                        {getStatusLabel(profile.status, profile.isPublic)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <span className="text-sm text-foreground">{profile.totalViews}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <span className="text-sm text-foreground">{profile.totalContacts}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-end text-sm font-medium">
                      <div className="flex items-center justify-end gap-2">
                        {profile.profileSlug && profile.isPublic && (
                          <button
                            onClick={() => handleViewProfile(profile)}
                            className="text-primary hover:text-primary/80 min-h-[44px] min-w-[44px] flex items-center justify-center"
                            title={t('View Profile')}
                            aria-label={t('View Profile')}
                          >
                            <ExternalLink className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => handleEditProfile(profile)}
                          className="text-muted-foreground hover:text-foreground min-h-[44px] min-w-[44px] flex items-center justify-center"
                          title={t('Manage Profile')}
                          aria-label={t('Manage Profile')}
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Statistics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
        <Card variant="elevated">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{t('Total Profiles')}</p>
                <p className="text-2xl font-bold text-foreground">{stats.total}</p>
              </div>
              <Users className="w-8 h-8 text-primary" />
            </div>
          </CardContent>
        </Card>

        <Card variant="elevated">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{t('Active Profiles')}</p>
                <p className="text-2xl font-bold text-success">{stats.active}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-success" />
            </div>
          </CardContent>
        </Card>

        <Card variant="elevated">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{t('Pending Approval')}</p>
                <p className="text-2xl font-bold text-warning">{stats.pending}</p>
              </div>
              <Clock className="w-8 h-8 text-warning" />
            </div>
          </CardContent>
        </Card>

        <Card variant="elevated">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{t('Draft Profiles')}</p>
                <p className="text-2xl font-bold text-muted-foreground">{stats.draft}</p>
              </div>
              <Edit className="w-8 h-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ProfileDirectoryTab;
